import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NotInterestedRoundedIcon from "@mui/icons-material/NotInterestedRounded";
import { Box, IconButton, LinearProgress, List, ListItem, Popover, Stack, TextareaAutosize, Tooltip, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import axios from 'axios';
import { isEqual } from 'lodash';
import * as React from 'react';
import discoveryAPI from '../../api/discovery-api';
// import * as appActions from '../../stores/app/actions';
import { DiscoverySession } from '../../stores/jotai/discovery-session';
import IwButton from '../app/IwButton';
import AlertDialog from '../app/alert-dialog';
import { PrettyDate } from '../assorted/pretty-date';
import MaterialAvatar from '../general/MaterialAvatar';

function transformLinks(message: string): string {
    return message;
}

interface Props {
    sentBySelf?: boolean;
    message: string;
    name: string;
    app: AppState;
    created: string;
    photoURL?: string;
    isLastMessageForUser?: boolean;
    id: string;
    chatID: string;
    channelID: string;
    discoverySession: DiscoverySession;
    messageEdited: number;
    deleted: number;
    onScrollRequest?: () => void;
    onEdit: (newMessageText: string) => void;
    onDelete: () => void;
    theme?: Theme
}

interface ChatMessage {}

interface State {
    editMode: boolean;
    showDeletionModal: boolean;
    focusEditMode: boolean;
    savingChat: boolean;
    messageCache: string;
    hasDeleted: boolean;
}

// 1: turn date into Date object, 2: ensure date's not in the future
const formatDate = (created: string): Date => {

    const createdDate = new Date(created);
    let date: Date = createdDate;

    // MP-562: If the created date is in the future, zero it out
    if (createdDate.valueOf() > (new Date()).valueOf()) {
        date = new Date();
    }

    return date;
};

/** Component to show details about the user that sent the message as well as the message, this is intended to be used as a ListItem */
export class ChatMessageView extends React.Component<Props & ChatMessage, State> {
    state: State = {
        editMode: false,
        showDeletionModal: false,
        savingChat: false,
        hasDeleted: false,
        messageCache: '',
        focusEditMode: false
    };

    componentDidMount = () => {
        // if (this.props.isLastMessageForUser) {
        //     appActions.setFlagUserEditLastMessage(0);
        // }

        this.setState({
            messageCache: this.props.message,
        });
    }

    componentDidUpdate = (prevProps: Props) => {
        if (prevProps.app !== undefined && this.props.app !== undefined) {
            if (prevProps.app?.userEditLastMessageFlag === 0 && this.props?.app?.userEditLastMessageFlag === 1) {
                if (this.props?.isLastMessageForUser) {
                    // Go into edit mode and focus on the edit
                    // appActions.setFlagUserEditLastMessage(0);

                    // 7/29/2021 JTB we're temporarily not using this feature
                    this.setState({
                        focusEditMode: true
                    });
                }
            }
        }

        if (prevProps.message !== this.props?.message) {
            this.setState({
                messageCache: this.props?.message,
            });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!isEqual(this.props, nextProps)) return true;
        if (!isEqual(this.state, nextState)) return true;
        return false;
    }

    handleSaveMessageEdit = () => {
        const { chatID, channelID, discoverySession, onEdit } = this.props;
        const { messageCache } = this.state;

        this.setState({
            savingChat: true
        });

        axios
            .put(
                `${process.env.NEXT_PUBLIC_LINX_URL}/chat`,
                {
                    id: chatID,
                    message: messageCache,
                    channelID: channelID,
                    sessionID: discoverySession?.sessionID,
                },
                {
                    withCredentials: false,
                    headers: {
                        Authorization: "Bearer " + discoverySession?.apiToken,
                    },
                }
            )
            .then(() => {
                onEdit(messageCache);

                this.setState({
                    editMode: false,
                    focusEditMode: false,
                });
            }).finally(() => {
                this.setState({
                    savingChat: false,
                });
            });
    }

    handleDeleteMessage = () => {
        const { chatID, channelID, discoverySession, onDelete } = this.props;

        this.setState({
            savingChat: true,
        });

        axios
            .delete(`${process.env.NEXT_PUBLIC_LINX_URL}/chat`, {
                headers: {
                    Authorization: "Bearer " + discoverySession?.apiToken,
                },
                data: {
                    id: chatID,
                    channelID: channelID,
                    sessionID: discoverySession?.sessionID,
                },
            })
            .then(() => {
                onDelete();
                this.setState({
                    showDeletionModal: false,
                });
            })
            .finally(() => {
                this.setState({
                    savingChat: false,
                });
            });
    }

    renderEditMode = () => {
        const { messageCache, savingChat } = this.state;
        const { message } = this.props;
        const { theme } = this.props;
        return (
            <Stack spacing={1}>
                <TextareaAutosize
                    value={messageCache}
                    autoFocus={true}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            this.setState({
                                savingChat: false,
                                editMode: false,
                                focusEditMode: false,
                                messageCache: this.props?.message,
                            });
                        } else if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey) {
                            this.handleSaveMessageEdit();
                            e.preventDefault();
                            return false;
                        }
                    }}
                    onChange={(e) => {
                        this.setState({
                            messageCache: e.target.value,
                        });
                    }}
                    style={{
                        fontSize: "14px",
                        lineHeight: 1.5,
                        resize: "none",
                        maxHeight: "160px",
                        overflow: "auto",
                        borderRadius: "4px",
                        borderColor: theme?.custom?.mainButtonHighlight,
                        outlineColor: theme?.custom?.mainButtonHighlight,
                        padding: "8px",
                    }}
                />
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    <IwButton
                        size="small"
                        onClick={() => {
                            this.setState({
                                editMode: false,
                                focusEditMode: false,
                                messageCache: this.props?.message,
                            });
                        }}
                        disabled={savingChat}
                    >
                        Cancel
                    </IwButton>
                    <IwButton
                        variant="contained"
                        size="small"
                        onClick={() => {
                            this.handleSaveMessageEdit();
                        }}
                        disabled={messageCache === message || savingChat}
                        loading={savingChat}
                    >
                        Save Changes
                    </IwButton>
                </Stack>
            </Stack>
        );
    }

    getMessage = () => {
        const {
            message
        } = this.props;

        const {
            messageCache
        } = this.state;

        if (messageCache === undefined || messageCache === null || messageCache === '') {
            return message;
        }

        return messageCache;
    }

    renderSavingPlaceholder = () => {
        return <span>
            <LinearProgress />
        </span>
    }

    render() {
        const { name, created, photoURL, sentBySelf, messageEdited, deleted } =
            this.props;
        const message = this.getMessage();
        const { theme } = this.props;
        if (!deleted && message === '') {
            return <span />
        }

        const {
            editMode,
            savingChat,
            showDeletionModal,
            hasDeleted,
            focusEditMode
        } = this.state;

        if (hasDeleted) { return <span /> }

        // MP-606: turn into an anchor all github style links, which look like: [anchor](source)
        const urlIfiedMessage = transformLinks(message);
        const date            = formatDate(created);

        const editWidget =
            editMode || focusEditMode ? (
                this.renderEditMode()
            ) : (
                <Typography
                    style={{
                        whiteSpace: "pre-wrap",
                        fontSize: "14px",
                        fontWeight: 400,
                    }}
                >
                    {urlIfiedMessage}
                </Typography>
            );

        return (
            <ListItem
                sx={{
                    justifyContent: "space-between",
                    padding: "24px 16px",
                }}
            >
                <div style={{ display: "flex", width: "100%" }}>
                    {!sentBySelf && (
                        <MaterialAvatar
                            sx={{
                                height: 40,
                                width: 40,
                                marginRight: "10px",
                            }}
                            user={{
                                name,
                                photoURL: discoveryAPI.artifactURL(photoURL),
                            }}
                        />
                    )}
                    <Stack
                        sx={{
                            alignItems: sentBySelf ? "flex-end" : "flex-start",
                            marginLeft: sentBySelf ? "auto" : 0,
                            position: "relative",
                            width: "100%",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: "bold",
                                fontSize: "12px",
                                marginBottom: "4px",
                                position: "absolute",
                                transform: "translateY(calc(-100% - 2px))",
                            }}
                        >
                            {name}
                        </Typography>
                        <Box
                            sx={{
                                background: sentBySelf
                                    ? !deleted
                                        ? theme?.custom?.mainButtonHighlight
                                        : theme?.custom?.deletedMessagePrimaryColor
                                    : !deleted
                                    ? theme?.custom?.disabledColor
                                    : theme?.custom?.deletedMessageSecondaryColor,
                                color:
                                    sentBySelf && !deleted
                                        ? theme?.custom?.white
                                        : theme?.custom?.text,
                                padding: "8px 16px",
                                borderRadius: sentBySelf
                                    ? "20px 4px 4px 20px"
                                    : "4px 20px 20px 4px",
                                alignItems: sentBySelf
                                    ? "flex-end"
                                    : "flex-start",
                                maxWidth: "80%",
                                marginLeft: sentBySelf ? "auto" : 0,
                                position: "relative",
                                ...(editMode && {
                                    backgroundColor:
                                    theme?.custom?.primary10,
                                    width: "100%",
                                    maxWidth: "100%",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    border: `1px dashed ${theme?.custom?.mainButtonHighlight}`,
                                }),
                            }}
                        >
                            {!deleted ? (
                                <>
                                    {editWidget}
                                    {!editMode && sentBySelf ? (
                                        <MaterialContextDropdown
                                            onEdit={() =>
                                                this.setState({
                                                    editMode: true,
                                                })
                                            }
                                            onDelete={() => {
                                                this.setState({
                                                    showDeletionModal: true,
                                                });
                                            }}
                                            sx={{
                                                position: "absolute",
                                                left: "0",
                                                top: "50%",
                                                transform:
                                                    "translate(calc(-100% - 24px), -50%)",
                                            }}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <Typography
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        opacity: 0.8,
                                        fontStyle: "italic",
                                    }}
                                >
                                    <NotInterestedRoundedIcon
                                        sx={{ marginRight: "8px" }}
                                    />
                                    This message was deleted
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            sx={{
                                color: theme?.custom?.gray60,
                                fontSize: "12px",
                                position: "absolute",
                                bottom: 0,
                                transform: "translateY(calc(100% + 2px))",
                                display: "flex",
                                gap: "4px",
                            }}
                        >
                            <PrettyDate>{date}</PrettyDate>
                            {!deleted && !!messageEdited && (
                                <Box component="span">(edited)</Box>
                            )}
                        </Typography>
                    </Stack>
                </div>
                <AlertDialog
                    open={showDeletionModal}
                    onClose={() => {
                        this.setState({
                            showDeletionModal: false,
                        });
                    }}
                    title="Want to delete?"
                    description="Are you sure you want to delete this chat message?"
                    confirmButton={
                        <IwButton
                            variant="contained"
                            onClick={this.handleDeleteMessage}
                            loading={savingChat}
                            disabled={savingChat}
                            sx={{
                                height: "auto",
                            }}
                        >
                            Delete
                        </IwButton>
                    }
                    abortButton={
                        <IwButton
                            onClick={() => {
                                this.setState({
                                    showDeletionModal: false,
                                });
                            }}
                            disabled={savingChat}
                            sx={{
                                height: "auto",
                            }}
                        >
                            Cancel
                        </IwButton>
                    }
                />
            </ListItem>
        );
    }
}


function MaterialContextDropdown(props) {
    const { sx = {} } = props;
    const [anchorEl, setAnchorEl] = React.useState(null)
    const theme = useTheme();
    const open = Boolean(anchorEl)

    return <Box style={{ alignSelf: 'flex-start', marginY: '10px', marginLeft: "auto", ...sx }} >
        <Tooltip title='See More Actions'>
            <IconButton sx={{ padding: '0px' }} onClick={(e) => {
                setAnchorEl(e.currentTarget)
            }}>
                <MoreHorizIcon/>
            </IconButton>
        </Tooltip>
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
                vertical: anchorEl && document.body.getBoundingClientRect().height - anchorEl.getBoundingClientRect().y > 100 ? 'bottom' : 'top',
                horizontal: 'right',
              }}
            transformOrigin={{
                vertical: anchorEl && document.body.getBoundingClientRect().height - anchorEl.getBoundingClientRect().y > 100 ? 'top' : 'bottom',
                horizontal: 'right',
            }}
            sx={{ 
                zIndex: theme?.custom?.menuZIndex,
                boxShadow: theme?.custom?.menuBoxShadow,
            }}
        >   
            <List sx={{ paddingY: '4px' }}>
                <ListItem sx={{ ':hover': { backgroundColor: theme?.custom?.hoverOptionColor } }} onClick={() => {
                    setAnchorEl(null)
                    props.onEdit()
                }}>
                    Edit
                </ListItem>
                <ListItem sx={{ ':hover': { backgroundColor: theme?.custom?.hoverOptionColor } }} onClick={() => {
                    setAnchorEl(null)
                    props.onDelete()
                }}>
                    Delete
                </ListItem>
            </List>    
        </Popover>
    </Box>
}
